import { PaletteMode, Components, Theme } from '@mui/material';
import { themePalette } from '../theme.palette';
import { themeShadows } from '../theme.shadows';
import dimensions from '../dimensions';
import { typographyTokens } from '../typography-tokens';

export const MuiAutocompleteTheme = (mode: PaletteMode): Components<Theme>['MuiAutocomplete'] => {
  const { componentTokens } = themePalette(mode);
  const shadows = themeShadows(mode);
  const { spacing } = dimensions;

  return {
    styleOverrides: {
      endAdornment: {
        '& .MuiIconButton-root': {
          borderRadius: spacing(2),
        },
      },
      inputRoot: {
        '& .MuiAutocomplete-input': {
          padding: '0 !important',
          minWidth: '40px',
          color: `${componentTokens.inputs.colors.normal.text.inputText.resting} !important`,
        },
      },
      root: {
        '& .MuiInputBase-input': {
          padding: '0 !important',
          width: 'auto',
        },
        // Target the label with specificity 0,4,0 to override TextField styles
        '& .MuiInputLabel-root.MuiInputLabel-formControl.MuiInputLabel-animated.MuiInputLabel-shrink':
          {
            fontWeight: typographyTokens.fontWeights.semibold,
            color: componentTokens.inputs.colors.normal.text.label.resting,
          },
      },
      paper: {
        // Dropdown menu styling - match MuiMenu
        backgroundColor: componentTokens.selectMenuItems.color.item.background.containerBase,
        border: `1px solid ${componentTokens.selectMenuItems.color.item.stroke.container.resting}`,
        borderRadius: spacing(1.5), // 6px
        boxShadow: shadows[23], // 16px blur with appropriate shadow color
        marginTop: spacing(2), // 8px
        marginBottom: spacing(2), // 8px
        padding: 0,
        overflow: 'auto',
        // Appear animation - matches MUI Select's Grow transition
        '@keyframes autocompleteGrow': {
          from: { opacity: 0, transform: 'scale(0.95)', transformOrigin: 'top center' },
          to: { opacity: 1, transform: 'scale(1)', transformOrigin: 'top center' },
        },
        transformOrigin: 'top center',
        animation: 'autocompleteGrow 200ms cubic-bezier(0.4, 0, 0.2, 1)',
        // Hide scrollbar
        scrollbarWidth: 'none', // Firefox
        msOverflowStyle: 'none', // IE/Edge
        '&::-webkit-scrollbar': {
          display: 'none', // Chrome, Safari, Opera
        },
        // Remove MUI's default Paper overlay effect
        '--Paper-overlay': 'none',
        backgroundImage: 'none',
        '&::before': {
          backgroundImage: 'none',
          display: 'none',
        },
      },
      listbox: {
        padding: '0 !important',
        // Hide scrollbar
        scrollbarWidth: 'none', // Firefox
        msOverflowStyle: 'none', // IE/Edge
        '&::-webkit-scrollbar': {
          display: 'none', // Chrome, Safari, Opera
        },
        // Align with Combobox (MuiMenuItem): resting, hover, selected
        '& .MuiAutocomplete-option': {
          justifyContent: 'space-between',
          fontFamily: typographyTokens.fontFamilies.sans,
          fontSize: typographyTokens.fontSizes.sm,
          lineHeight: typographyTokens.lineHeights.sm,
          letterSpacing: typographyTokens.letterSpacings.normal,
          borderColor: componentTokens.selectMenuItems.color.item.stroke.row.resting,
          backgroundColor: componentTokens.selectMenuItems.color.item.background.row.resting,
          color: componentTokens.selectMenuItems.color.item.text.row.resting,
          '&:hover': {
            backgroundColor: componentTokens.selectMenuItems.color.item.background.row.hover,
            borderColor: componentTokens.selectMenuItems.color.item.stroke.row.hover,
            color: componentTokens.selectMenuItems.color.item.text.row.hover,
          },
          '&.Mui-focused, &[data-focus="true"]': {
            backgroundColor: componentTokens.selectMenuItems.color.item.background.row.hover,
            borderColor: componentTokens.selectMenuItems.color.item.stroke.row.hover,
            color: componentTokens.selectMenuItems.color.item.text.row.hover,
          },
          '&[aria-selected="true"]': {
            backgroundColor: componentTokens.selectMenuItems.color.item.background.row.selected,
            borderColor: componentTokens.selectMenuItems.color.item.stroke.row.selected,
            color: componentTokens.selectMenuItems.color.item.text.row.selected,
          },
        },
      },
      popupIndicator: {
        color: componentTokens.buttons.colors.coreIcon.ghost.text.resting,
        backgroundColor: componentTokens.buttons.colors.coreIcon.ghost.background.resting,
        borderColor: componentTokens.buttons.colors.coreIcon.ghost.stroke.resting,
        '& .MuiSvgIcon-root': {
          color: componentTokens.buttons.colors.coreIcon.ghost.assets.resting,
          fontSize: '20px',
        },
        '&:hover': {
          color: componentTokens.buttons.colors.coreIcon.ghost.text.hover,
          backgroundColor: componentTokens.buttons.colors.coreIcon.ghost.background.hover,
          borderColor: componentTokens.buttons.colors.coreIcon.ghost.stroke.hover,
          '& .MuiSvgIcon-root': {
            color: componentTokens.buttons.colors.coreIcon.ghost.assets.hover,
          },
        },
        '&:focus': {
          color: componentTokens.buttons.colors.coreIcon.ghost.text.hover,
          backgroundColor: componentTokens.buttons.colors.coreIcon.ghost.background.hover,
          borderColor: componentTokens.buttons.colors.coreIcon.ghost.stroke.hover,
          '& .MuiSvgIcon-root': {
            color: componentTokens.buttons.colors.coreIcon.ghost.assets.hover,
          },
        },
        '&:active, &[data-active="true"]': {
          color: componentTokens.buttons.colors.coreIcon.ghost.text.selected,
          backgroundColor: componentTokens.buttons.colors.coreIcon.ghost.background.selected,
          borderColor: componentTokens.buttons.colors.coreIcon.ghost.stroke.selected,
          '& .MuiSvgIcon-root': {
            color: componentTokens.buttons.colors.coreIcon.ghost.assets.selected,
          },
        },
        '&:disabled': {
          color: componentTokens.buttons.colors.coreIcon.ghost.text.resting,
          opacity: 0.4,
        },
      },
      popper: {
        minWidth: 'max-content',
        '& .MuiAutocomplete-option': {
          display: 'flex',
        },
      },
    },
  };
};
